
import {
  CATEGORY_LABELS,
  executeOrphanDeletion,
  listOrphanFindings,
  OrphanExecuteResult,
  OrphanFindingJson,
  OrphanPreview,
  OrphanScanRunJson,
  previewOrphanDeletion,
  REASON_LABELS,
  STATUS_LABELS
} from "@/api/orphanSubcollections";
import OrphanBulkDeletionModal, {
  BulkOutcome
} from "@/components/orphan_subcollections/OrphanBulkDeletionModal.vue";
import OrphanDeletionModal from "@/components/orphan_subcollections/OrphanDeletionModal.vue";
import MButton from "@/components/MButton.vue";
import store from "@/store";
import { Options, Vue } from "vue-class-component";

// 1 回の選択の上限。1 件ごとに確認と削除を呼ぶので、時間と誤操作の範囲を抑える
const BULK_MAX = 50;

@Options({
  components: { MButton, OrphanDeletionModal, OrphanBulkDeletionModal },
  // 絞り込みを変えたら、見えない行が選ばれたままにならないように、選択を解除する
  watch: {
    schoolFilter() {
      (this as HandleOrphanSubcollections).selectedIds = [];
    },
    statusFilter() {
      (this as HandleOrphanSubcollections).selectedIds = [];
    },
    categoryFilter() {
      (this as HandleOrphanSubcollections).selectedIds = [];
    }
  }
})
export default class HandleOrphanSubcollections extends Vue {
  findings: OrphanFindingJson[] = [];
  latestRun: OrphanScanRunJson | null = null;
  statusFilter = "detected";
  categoryFilter = "";
  schoolFilter = "";
  statusLabels = STATUS_LABELS;
  categoryLabels = CATEGORY_LABELS;

  preview: OrphanPreview | null = null;
  running = false;
  result: OrphanExecuteResult | null = null;

  // 複数選択して、まとめて削除する
  bulkMax = BULK_MAX;
  selectedIds: string[] = [];
  bulkStage: "" | "checking" | "confirm" | "running" | "done" = "";
  bulkProgress = { done: 0, total: 0 };
  bulkDeletable: OrphanPreview[] = [];
  bulkExcluded: { path: string; reason: string }[] = [];
  bulkOutcome: BulkOutcome = { deleted: [], refused: [], errors: [] };

  async created() {
    await this.load();
  }

  get filtered(): OrphanFindingJson[] {
    return this.findings.filter(
      f =>
        (!this.schoolFilter || f.schoolId === this.schoolFilter) &&
        (!this.statusFilter || f.status === this.statusFilter) &&
        (!this.categoryFilter || f.category === this.categoryFilter)
    );
  }

  // 学校の絞り込みの選択肢。検知結果にある学校だけを、名前の順に並べる
  get schoolOptions(): { id: string; label: string }[] {
    // Map のイテレータは、この TypeScript の設定（ES5）では展開できないので、配列に詰める
    const schools: { id: string; label: string }[] = [];
    for (const f of this.findings) {
      if (!schools.some(school => school.id === f.schoolId)) {
        schools.push({ id: f.schoolId, label: this.schoolLabel(f) });
      }
    }
    return schools.sort((a, b) => a.label.localeCompare(b.label, "ja"));
  }

  schoolLabel(finding: OrphanFindingJson): string {
    return finding.schoolName ?? `（学校名なし: ${finding.schoolId}）`;
  }

  classroomLabel(finding: OrphanFindingJson): string {
    return finding.classroomName ?? `（教室名なし: ${finding.classroomId}）`;
  }

  // 選べる行: 削除の対象にできる状態で、唯一のデータではないもの
  isSelectable(finding: OrphanFindingJson): boolean {
    return this.canOpen(finding) && finding.category !== "unmoved_only_copy";
  }

  isSelected(id: string): boolean {
    return this.selectedIds.indexOf(id) >= 0;
  }

  get selectableCount(): number {
    return this.filtered.filter(f => this.isSelectable(f)).length;
  }

  get allSelected(): boolean {
    return (
      this.selectableCount > 0 &&
      (this.selectedIds.length >= this.bulkMax ||
        this.selectedIds.length >= this.selectableCount)
    );
  }

  get limitNotice(): boolean {
    return (
      this.selectedIds.length >= this.bulkMax &&
      this.selectableCount > this.selectedIds.length
    );
  }

  checkboxDisabled(finding: OrphanFindingJson): boolean {
    if (this.isSelected(finding.id)) return false;
    return (
      !this.isSelectable(finding) || this.selectedIds.length >= this.bulkMax
    );
  }

  checkboxTitle(finding: OrphanFindingJson): string {
    if (finding.category === "unmoved_only_copy") {
      return "唯一のデータの可能性があるため、選べません";
    }
    if (!this.canOpen(finding)) return "この状態では、選べません";
    return "";
  }

  toggle(id: string) {
    if (this.isSelected(id)) {
      this.selectedIds = this.selectedIds.filter(x => x !== id);
    } else if (this.selectedIds.length < this.bulkMax) {
      this.selectedIds = this.selectedIds.concat([id]);
    }
  }

  // 表示している（絞り込み後の）行のうち、選べるものを、先頭から上限まで選ぶ
  toggleAll() {
    if (this.allSelected) {
      this.selectedIds = [];
      return;
    }
    const ids: string[] = [];
    for (const f of this.filtered) {
      if (ids.length >= this.bulkMax) break;
      if (this.isSelectable(f)) ids.push(f.id);
    }
    this.selectedIds = ids;
  }

  // ① 選んだ分を、1 件ずつ順に確認する（並列にしない）
  async startBulk() {
    if (this.selectedIds.length === 0 || this.bulkStage) return;
    const targets = this.findings.filter(f => this.isSelected(f.id));
    this.bulkDeletable = [];
    this.bulkExcluded = [];
    this.bulkOutcome = { deleted: [], refused: [], errors: [] };
    this.bulkProgress = { done: 0, total: targets.length };
    this.bulkStage = "checking";
    for (const finding of targets) {
      try {
        const preview = await previewOrphanDeletion(finding.id);
        if (!preview.deletable) {
          this.bulkExcluded.push({
            path: finding.path,
            reason: this.reasonLabel(preview.reason)
          });
        } else if (preview.truncated) {
          // 文書が多すぎて数え切れない行は、1 件の画面と同じく、実行しない
          this.bulkExcluded.push({
            path: finding.path,
            reason:
              "文書が多いため、一括では削除できません。1 件ずつ確認してください"
          });
        } else {
          this.bulkDeletable.push(preview);
        }
      } catch (e) {
        console.error(e);
        this.bulkExcluded.push({
          path: finding.path,
          reason: `確認に失敗しました: ${this.errorMessage(e)}`
        });
      }
      this.bulkProgress = {
        done: this.bulkProgress.done + 1,
        total: targets.length
      };
    }
    this.bulkStage = "confirm";
  }

  // ③ 確認で見た件数のまま、1 件ずつ順に削除する。失敗しても、残りを続ける
  async executeBulk() {
    if (this.bulkStage !== "confirm") return;
    const targets = this.bulkDeletable.slice();
    this.bulkProgress = { done: 0, total: targets.length };
    this.bulkStage = "running";
    for (const preview of targets) {
      try {
        const result = await executeOrphanDeletion(
          preview.findingId,
          preview.docCount
        );
        if (result.executed) {
          this.bulkOutcome.deleted.push({
            path: preview.path,
            count: result.deletedCount
          });
        } else {
          this.bulkOutcome.refused.push({
            path: preview.path,
            reason: this.reasonLabel(result.reason)
          });
        }
      } catch (e) {
        console.error(e);
        this.bulkOutcome.errors.push({
          path: preview.path,
          message: this.errorMessage(e)
        });
      }
      this.bulkProgress = {
        done: this.bulkProgress.done + 1,
        total: targets.length
      };
    }
    this.bulkStage = "done";
  }

  // ④ 結果を閉じたら、一覧を読み直し、選択を解除する。確認の段階で閉じたときは、何も変えない
  async closeBulk() {
    if (this.bulkStage === "checking" || this.bulkStage === "running") return;
    const finished = this.bulkStage === "done";
    this.bulkStage = "";
    if (finished) {
      this.selectedIds = [];
      await this.load();
    }
  }

  reasonLabel(reason?: string): string {
    return (reason && REASON_LABELS[reason]) || reason || "理由が不明です";
  }

  errorMessage(e: unknown): string {
    return e instanceof Error ? e.message : "unknown error";
  }

  // 削除の対象にできる状態だけ、確認へ進める
  canOpen(finding: OrphanFindingJson): boolean {
    return finding.status === "detected" || finding.status === "failed";
  }

  categoryBadgeClass(category: string): string {
    return category === "moved_leftover"
      ? "bg-green-100 text-green-700"
      : category === "ownerless"
      ? "bg-yellow-100 text-yellow-700"
      : "bg-red-100 text-red-700";
  }

  formatDate(iso?: string): string {
    return iso ? new Date(iso).toLocaleString("ja-JP") : "-";
  }

  moveToHome() {
    this.$router.push({ name: "home" });
  }

  async load() {
    try {
      store.commit("START_LOADING", "読み込み中...");
      const data = await listOrphanFindings();
      this.findings = data.findings;
      this.latestRun = data.latestRun;
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
    } finally {
      store.commit("END_LOADING");
    }
  }

  async openPreview(finding: OrphanFindingJson) {
    try {
      store.commit("START_LOADING", "内容を確認中...");
      this.result = null;
      this.preview = await previewOrphanDeletion(finding.id);
    } catch (e) {
      console.error(e);
      const message = e instanceof Error ? e.message : "unknown error";
      alert(`エラーが発生しました。\n${message}`);
    } finally {
      store.commit("END_LOADING");
    }
  }

  async execute(expectedDocCount: number) {
    if (!this.preview || this.running) return;
    try {
      this.running = true;
      store.commit("START_LOADING", "削除中...");
      this.result = await executeOrphanDeletion(
        this.preview.findingId,
        expectedDocCount
      );
    } catch (e) {
      console.error(e);
      const message = e instanceof Error ? e.message : "unknown error";
      alert(`エラーが発生しました。\n${message}`);
    } finally {
      this.running = false;
      store.commit("END_LOADING");
    }
  }

  async closeModal() {
    const executed = this.result !== null;
    this.preview = null;
    this.result = null;
    // 実行したあとは、一覧の状態を更新する
    if (executed) await this.load();
  }
}
